import React, { FC, useId, useMemo } from 'react';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import type { Option } from '@rover/components/src/formFields/ButtonGroup';
import ButtonGroup from '@rover/components/src/formFields/ButtonGroup';
import { SERVICE_TYPE_CHOICES } from '@rover/constants/src/translations/service.constants';
import FilterLabel from '@rover/shared/js/search/components/FilterLabel';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

type Props = {
  onChange: (spacesRequired: number) => void;
  searchFilters: Partial<SearchFilters>;
  mb?: string;
};

export const SERVICE_TYPE_LABELS: Record<string, MessageDescriptor> = {
  [SERVICE_TYPE_CHOICES.boarding]: msg`How many pets?`,
  [SERVICE_TYPE_CHOICES.dropIn]: msg`How many visits per day?`,
  [SERVICE_TYPE_CHOICES.dayCare]: msg`How many pets?`,
  [SERVICE_TYPE_CHOICES.walking]: msg`How many walks per day?`,
};

const SpacesRequired: FC<Props> = ({ searchFilters, onChange, mb }) => {
  const { i18n } = useLingui();
  const legendId = useId();
  const { spacesRequired, serviceType: serviceTypeSlug } = searchFilters;
  const labelDescriptor = SERVICE_TYPE_LABELS[`${serviceTypeSlug}`];
  const label = labelDescriptor ? i18n._(labelDescriptor) : '';

  const options = useMemo(
    (): Option[] =>
      [1, 2, 3].map((key) => ({
        key: `${key}-spaces-required`,
        label: key === 3 ? `${key}+` : `${key}`,
        value: key.toString(),
      })),
    []
  );

  return (
    <>
      {label && (
        <FilterLabel id={legendId} mb={mb}>
          {label}
        </FilterLabel>
      )}
      <ButtonGroup
        data-testid="spaces-required"
        buttonType="radio"
        legendId={legendId}
        name="spaces-required"
        onToggle={(option) => onChange(parseInt(`${option.value}`, 10))}
        options={options}
        value={spacesRequired?.toString()}
      />
    </>
  );
};

export default SpacesRequired;
